import { Holding, valuationStatusOf } from "../../../../../services/holdings";
import { InstrumentSummary } from "../../../../../services/instruments/types";
import {
  isOverrideRecoverable,
  priceUpdatedFor,
  valuationAmountText,
  valuationBadgeFor,
  valuationDetailFor,
} from "../../../../../services/valuation";
import {
  colorForCategory,
  formatCurrency,
  formatPercent,
  formatSignedPercent,
} from "../../../../../utils";
import {
  formatUnitPrice,
  openedBeforePriceHistoryHorizon,
  signedCurrencyOrDash,
  trimQuantity,
} from "../../../utils";
import {
  ALLOCATION_UNAVAILABLE_LABEL,
  INSTRUMENT_MARK_LETTERS,
  PRICE_OVERRIDE_BLOCKED_LABEL,
  PRICE_OVERRIDE_ENTRY_LABEL,
} from "./constants";
import { HoldingDisplay, PriceOverrideEntry, ReturnTone } from "./types";

// A pre-horizon holding always qualifies (no shared price will ever cover those months); a
// post-horizon one only qualifies when its valuation is actually blocked on coverage.
export const canRepairWithPrivatePrice = (holding: Holding): boolean =>
  openedBeforePriceHistoryHorizon(holding.openedAt) ||
  isOverrideRecoverable(valuationStatusOf(holding));

// Bound once here so the table row and mobile card, which render it separately, can't disagree
// about when it appears or what it says.
export const priceOverrideEntryFor = (
  holding: Holding,
  onSetPrice?: (holding: Holding) => void,
): PriceOverrideEntry | null => {
  if (!onSetPrice || !canRepairWithPrivatePrice(holding)) {
    return null;
  }
  const label = openedBeforePriceHistoryHorizon(holding.openedAt)
    ? PRICE_OVERRIDE_ENTRY_LABEL
    : PRICE_OVERRIDE_BLOCKED_LABEL;
  return { label, open: () => onSetPrice(holding) };
};

// The one colour an instrument wears across the page, so the bar and any mark can't drift.
export const instrumentMarkColorOf = (instrument: InstrumentSummary): string =>
  colorForCategory(instrument.symbol);

export const accountShareText = (percent: number | null): string =>
  `${formatPercent(percent)} of account`;

// Keyed off the € amount, the headline figure: the percent is null on a zero cost basis and can round to 0.0%.
const returnToneOf = (returnValue: number | null): ReturnTone => {
  if (returnValue === null || returnValue === 0) {
    return "neutral";
  }
  return returnValue > 0 ? "positive" : "negative";
};

// An unvalued position already shows its own note and private-price remedy, so no share line competes with them.
const allocationTextFor = ({
  positionValue,
  allocationPercent,
}: Holding): string | null => {
  if (positionValue === null) {
    return null;
  }
  return allocationPercent === null
    ? ALLOCATION_UNAVAILABLE_LABEL
    : accountShareText(allocationPercent);
};

// Value keys off the valuation state, not `currentValue` being null, since only the state
// distinguishes an in-flight quote from a blocked correction; price mirrors the live quote independently.
export const buildHoldingDisplay = (holding: Holding): HoldingDisplay => {
  const valuation = valuationStatusOf(holding);
  const badge = valuationBadgeFor(valuation);
  const price = holding.latestPriceEur;

  return {
    valueText: valuationAmountText({ value: holding.positionValue, valuation }),
    badge,
    valuationMessage: badge ? valuationDetailFor(valuation) : null,
    // formatCurrency(null) would print "0,00 €" instead of a placeholder, so absence routes
    // through valuationAmountText for the right dash/"Unavailable" text.
    priceText:
      price !== null
        ? formatUnitPrice(price)
        : valuationAmountText({ value: null, valuation }),
    priceUpdated: price !== null ? priceUpdatedFor(holding) : null,
    instrumentSubtitle: holding.instrument.name,
    quantityText: trimQuantity(holding.quantity),
    costBasisText:
      holding.remainingCostBasis === null
        ? "—"
        : formatCurrency(holding.remainingCostBasis),
    allocationText: allocationTextFor(holding),
    markText: holding.instrument.symbol.slice(0, INSTRUMENT_MARK_LETTERS),
    markColor: instrumentMarkColorOf(holding.instrument),
    returnAmountText: signedCurrencyOrDash(holding.unrealizedReturnValue),
    returnPercentText:
      holding.unrealizedReturnPercent === null
        ? null
        : formatSignedPercent(holding.unrealizedReturnPercent),
    returnTone: returnToneOf(holding.unrealizedReturnValue),
  };
};
